// Einstellungen mit zweiter Navigationsspalte (S-01, Runde 4 Vorarbeit): links die Gruppen mit ihren Seiten wie in
// Close, rechts die gewählte Seite unter /einstellungen/<seite>. Welche Seiten es gibt, steht in
// pages/einstellungen/seiten.ts; die Seiten selbst liegen als eigene Dateien daneben (früher alles in dieser Datei).
// Alte Adressen mit Anker (/einstellungen#close) führen weiter auf die richtige Seite (EinstellungenStart).
import { Suspense, useEffect, useMemo } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Laden } from "../components/Bausteine";
import { Icon, type IconName } from "../components/Icon";
import { useSeitenKopf } from "../daten/seite";
import { T } from "../texte";
import EinstellungenStart from "./einstellungen/EinstellungenStart";
import { GRUPPEN, sichtbareSeiten, STANDARD_SEITE } from "./einstellungen/seiten";
import "./einstellungen/einstellungen.css";

/** Symbol je Gruppe in der zweiten Spalte (wie Close: Zahnrad, Team, Telefon, Verbindung) */
const GRUPPEN_SYMBOL: Record<string, IconName> = { allgemein: "einstellungen", organisation: "person", anpassung: "felder", kommunikation: "telefon", verbinden: "link" };

/** Springt zum Anker (#close …), sobald die Seite geladen ist (die Seiten werden erst beim Öffnen nachgeladen). */
function useAnkerSprung(hash: string, pfad: string): void {
  useEffect(() => {
    const id = hash.replace(/^#/, "");
    if (!id) return;
    let versuche = 0;
    const t = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ block: "start" });
        window.clearInterval(t);
      } else if (++versuche >= 15) {
        window.clearInterval(t);
      }
    }, 80);
    return () => window.clearInterval(t);
  }, [hash, pfad]);
}

export default function Einstellungen() {
  const { nutzer } = useAuth();
  const ort = useLocation();
  const seiten = useMemo(() => sichtbareSeiten(nutzer?.permissions ?? []), [nutzer]);
  const aktuell = seiten.find((s) => ort.pathname === `/einstellungen/${s.pfad}` || ort.pathname.startsWith(`/einstellungen/${s.pfad}/`));
  useSeitenKopf(
    aktuell ? [{ label: T.nav.einstellungen, to: `/einstellungen/${STANDARD_SEITE}` }, { label: aktuell.titel }] : [{ label: T.nav.einstellungen }],
  );
  useAnkerSprung(ort.hash, ort.pathname);

  if (!nutzer) return null;
  return (
    <div className="einst">
      <nav className="einst-nav" aria-label="Einstellungen">
        {GRUPPEN.map((g) => {
          const liste = seiten.filter((s) => s.gruppe === g.id);
          if (!liste.length) return null;
          return (
            <div key={g.id} className="einst-nav__gruppe">
              <div className="einst-nav__titel">
                {GRUPPEN_SYMBOL[g.id] ? <Icon name={GRUPPEN_SYMBOL[g.id]} groesse={13} /> : null}
                {g.titel}
              </div>
              {liste.map((s) => (
                <NavLink key={s.pfad} to={`/einstellungen/${s.pfad}`} className="einst-nav__link">
                  {s.titel}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="einst-seite">
        {aktuell ? (
          <header className="einst-kopf">
            <h1>{aktuell.titel}</h1>
            {aktuell.untertitel ? <p className="seite__text">{aktuell.untertitel}</p> : null}
          </header>
        ) : null}
        <div className="einst-seite__inhalt">
          <Suspense fallback={<Laden />}>
            <Routes>
              <Route index element={<EinstellungenStart />} />
              {seiten.map((s) => (
                <Route key={s.pfad} path={s.pfad} element={<s.komponente />} />
              ))}
              <Route path="*" element={<Navigate to={`/einstellungen/${STANDARD_SEITE}`} replace />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
}
